/**
 * Canonical status -> color maps for EdgeLMS (design system v1, docs/DESIGN_SYSTEM.md §1.1).
 *
 * Single source of truth. Do NOT re-declare STATUS_COLORS in pages — import from here, or
 * (preferred) render <StatusBadge status=... kind=... />. Classes use the `bg-{hue}-100
 * text-{hue}-800` pattern and are kept in tailwind.config safelist so PurgeCSS can't drop them.
 */

const tint = (hue) => `bg-${hue}-100 text-${hue}-800`;

export const NEUTRAL = "bg-slate-100 text-slate-700"; // unknown status -> never transparent

// ---- Order lifecycle (union of AdminOrders + OrderDetail) ----
export const ORDER_STATUS = {
  CREATED: tint("blue"),
  MANIFESTED: tint("purple"),
  PICKED_UP: tint("yellow"),
  LEFT_ORIGIN: tint("orange"),
  REACH_DESTINATION: tint("cyan"),
  UNDEL_REATTEMPT: tint("amber"),
  PART_DEL: tint("lime"),
  OFD: tint("teal"),
  DELIVERED: tint("green"),
  RETURNED_INTRANSIT: tint("red"),
  RECEIVED_AT_RETURN_CENTER: tint("pink"),
  RETURN_OFD: tint("rose"),
  RETURN_DELIVERED: tint("violet"),
  NOT_PICKED: tint("gray"),
  LOST: "bg-red-200 text-red-900",
  CANCELLED: "bg-gray-200 text-gray-900",
  APPOINTMENT_SET: tint("indigo"),
  APPOINTMENT_UPDATED: tint("indigo"),
  APPOINTMENT_SCHEDULED: tint("indigo"),
  SCHEDULED: tint("indigo"),
};

// ---- B2C order lifecycle (b2c_orders, B2C_ORDER_STATUSES) ----
// Distinct from the 15 B2B ORDER_STATUSES. Hues mirror the lifecycle semantics:
// New = neutral, AWB assigned = blue, pickup/transit = warm progression, delivered = green, RTO = red family.
export const B2C_ORDER_STATUS = {
  NEW: NEUTRAL,
  AWB_ASSIGNED: tint("blue"),
  PICKUP_SCHEDULED: tint("indigo"),
  PICKED_UP: tint("yellow"),
  IN_TRANSIT: tint("cyan"),
  OUT_FOR_DELIVERY: tint("teal"),
  DELIVERED: tint("green"),
  NDR: tint("amber"),
  RTO_INITIATED: tint("orange"),
  RTO_IN_TRANSIT: tint("rose"),
  RTO_DELIVERED: tint("violet"),
  CANCELLED: "bg-gray-200 text-gray-900",
  LOST: "bg-red-200 text-red-900",
};

export const GRN_STATUS = {
  PENDING: tint("amber"),
  PARTIAL: tint("blue"),
  COMPLETE: tint("green"),
  REJECTED: tint("red"),
};

export const PICKUP_STATUS = {
  CREATED: NEUTRAL,
  SCHEDULED: tint("blue"),
  OUT_FOR_PICKUP: tint("yellow"),
  PICKED: tint("green"),
  NOT_PICKED: tint("red"),
  CANCELLED: tint("gray"),
};

// ---- PharmEasy trip / shipment / session / escalation ----
export const TRIP_STATUS = {
  CREATED: NEUTRAL,
  OUT_FOR_DELIVERY: tint("blue"),
  RECON_STARTED: tint("purple"),
  RECON_FINISHED: tint("purple"),
  DELIVERED: tint("green"),
  COMPLETED: tint("green"),
  CANCELLED: tint("red"),
  REJECTED: tint("red"),
  PENDING: tint("amber"),
};

export const SHIPMENT_STATUS = {
  DELIVERED: tint("green"),
  OUT_FOR_DELIVERY: tint("blue"),
  PENDING: tint("amber"),
  ATTEMPTED: tint("orange"),
  CANCELLED: tint("red"),
  REJECTED: tint("red"),
  RTO: tint("red"),
};

export const SESSION_STATUS = {
  PENDING: tint("amber"),
  IN_PROGRESS: tint("blue"),
  COMPLETED: tint("green"),
  FORCE_COMPLETED: tint("amber"),
};

export const ESCALATION_STATUS = {
  OPEN: tint("blue"),
  IN_PROGRESS: tint("amber"),
  RESOLVED: tint("green"),
  CLOSED: NEUTRAL,
  REOPENED: tint("red"),
};

// Background (bulk) job lifecycle — PENDING → PROCESSING → COMPLETED | FAILED | CANCELLED.
export const JOB_STATUS = {
  PENDING: tint("amber"),
  PROCESSING: tint("blue"),
  COMPLETED: tint("green"),
  FAILED: tint("red"),
  CANCELLED: NEUTRAL,
};

const KIND_MAPS = {
  order: ORDER_STATUS,
  b2c: B2C_ORDER_STATUS,
  trip: TRIP_STATUS,
  shipment: SHIPMENT_STATUS,
  session: SESSION_STATUS,
  grn: GRN_STATUS,
  pickup: PICKUP_STATUS,
  escalation: ESCALATION_STATUS,
  job: JOB_STATUS,
  generic: ORDER_STATUS,
};

/** Normalize a status string to a map key: upper-case, spaces/hyphens -> underscore. */
export function normalizeStatus(status) {
  return String(status ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
}

/** Returns the `bg-* text-*` class string for a status of the given kind (neutral fallback). */
export function statusClasses(status, kind = "order") {
  const map = KIND_MAPS[kind] || ORDER_STATUS;
  return map[normalizeStatus(status)] || NEUTRAL;
}

/** Human label: TITLE_CASE underscores -> spaced Title Case. */
export function statusLabel(status) {
  return String(status ?? "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Recharts needs hex fills, not classes — parallel map derived from the same hues.
export const CHART_STATUS_HEX = {
  CREATED: "#3b82f6",
  MANIFESTED: "#a855f7",
  PICKED_UP: "#eab308",
  LEFT_ORIGIN: "#f97316",
  REACH_DESTINATION: "#06b6d4",
  UNDEL_REATTEMPT: "#f59e0b",
  PART_DEL: "#84cc16",
  OFD: "#14b8a6",
  OUT_FOR_DELIVERY: "#3b82f6",
  DELIVERED: "#10b981",
  COMPLETED: "#10b981",
  RETURNED_INTRANSIT: "#ef4444",
  CANCELLED: "#6b7280",
  REJECTED: "#ef4444",
  RTO: "#ef4444",
  LOST: "#b91c1c",
  PENDING: "#f59e0b",
  NOT_PICKED: "#9ca3af",
};

export function chartHex(status) {
  return CHART_STATUS_HEX[normalizeStatus(status)] || "#94a3b8";
}
